import React, { useState, useCallback, useEffect } from 'react';
import { SERVICE_CALLS, clearCache } from 'extension-helpers';
import { DEFAULT_LANGUAGE_SETTING, EMAIL_FREQUENCY_SETTING, PROSPECT_RECORDING_SETTING, SettingsUpdate } from "interfaces/users";
import { Typography } from 'interfaces/typography';
import { useDispatch } from 'react-redux';
import { getServicesManager } from 'services';
import { updateTeam } from 'lib/redux/users/slice';
import { useUserData } from 'lib/redux/users/user-data-hook';
import { SettingsSection, SettingRow, StandardContainer } from 'utils/CommonUIComponents';

export enum TEAM_BACKEND_SETTINGS {
  DEFAULT_AUDIO_SETTINGS = 'Persist audio recording',
  REALTIME_TRANSCRIPTION = 'Real-time transcription',
  TRANSCRIPTION_RECORDING = 'Persist transcription',
  LANGUAGE_SETTING = 'Default language',
  EMAIL_NOTIFICATION_SETTINGS = 'Notify on review changes?',
  DATA_SEARCH_ENABLED = 'Disable LinkedIn finder?',
  DISABLE_AUTODIALER = 'Disable Autodialer?',
}

const TEAM_SETTING_TOOLTIPS: {[key: string]: string} = {
  [TEAM_BACKEND_SETTINGS.DEFAULT_AUDIO_SETTINGS]: 'Choose which calls to automatically record by default',
  [TEAM_BACKEND_SETTINGS.REALTIME_TRANSCRIPTION]: 'Enable live transcription during your calls for real-time insights',
  [TEAM_BACKEND_SETTINGS.TRANSCRIPTION_RECORDING]: 'Save call transcripts automatically for future reference and analysis',
  [TEAM_BACKEND_SETTINGS.LANGUAGE_SETTING]: 'Set the primary language for transcription and coaching insights',
  [TEAM_BACKEND_SETTINGS.EMAIL_NOTIFICATION_SETTINGS]: 'Get notified via email when call review statuses change',
  [TEAM_BACKEND_SETTINGS.DATA_SEARCH_ENABLED]: 'Disable automatic LinkedIn profile search and enrichment for prospects',
  [TEAM_BACKEND_SETTINGS.DISABLE_AUTODIALER]: 'Completely disable autodialer functionality for your team (admin only)',
}

type TeamSetting = {
  backendSetting: TEAM_BACKEND_SETTINGS
}

type TeamSettingSection = {
  settings: TeamSetting[]
  label: string
}

const DEFAULT_LANGUAGE_SETTING_TO_LABEL: {[key in DEFAULT_LANGUAGE_SETTING]: string} = {
  [DEFAULT_LANGUAGE_SETTING.ENGLISH]: 'English',
  [DEFAULT_LANGUAGE_SETTING.SPANISH]: 'Spanish',
  [DEFAULT_LANGUAGE_SETTING.FRENCH]: "French",
  [DEFAULT_LANGUAGE_SETTING.MULTI]: "Auto-detect",
}

const PROSPECT_RECORDING_SETTING_TO_LABEL: {[key in PROSPECT_RECORDING_SETTING]: string} = {
  [PROSPECT_RECORDING_SETTING.ALL]: 'All calls',
  [PROSPECT_RECORDING_SETTING.NONE]: 'No calls',
  [PROSPECT_RECORDING_SETTING.ONE_PARTY_ONLY]: 'All 1-party calls',
  [PROSPECT_RECORDING_SETTING.DIALER]: "Mirror dialer settings"
}

export function TeamSettings() {
  const dispatch = useDispatch()
  const { team } = useUserData()
  const [updatingSettings, setUpdatingSettings] = useState<Set<TEAM_BACKEND_SETTINGS>>(new Set())

  const updateSettingsResult = (updates: Map<TEAM_BACKEND_SETTINGS, string | boolean>) => {
    const updatingKeys = Array.from(updates.keys())
    setUpdatingSettings(prev => new Set([...prev, ...updatingKeys]))

    const settingUpdate: SettingsUpdate = {
      'is_team': true
    }

    for (const [key, value] of updates) {
      switch (key) {
        case TEAM_BACKEND_SETTINGS.DEFAULT_AUDIO_SETTINGS: settingUpdate['default_prospect_recording'] = value as PROSPECT_RECORDING_SETTING; break
        case TEAM_BACKEND_SETTINGS.REALTIME_TRANSCRIPTION: settingUpdate['default_prospect_streaming_transcription'] = value as PROSPECT_RECORDING_SETTING; break
        case TEAM_BACKEND_SETTINGS.TRANSCRIPTION_RECORDING: settingUpdate['default_prospect_transcript_persistence'] = value as PROSPECT_RECORDING_SETTING; break
        case TEAM_BACKEND_SETTINGS.LANGUAGE_SETTING: settingUpdate['default_language'] = value as DEFAULT_LANGUAGE_SETTING; break
        case TEAM_BACKEND_SETTINGS.DATA_SEARCH_ENABLED: settingUpdate['linkedin_finder_disabled'] = value as boolean; break
        case TEAM_BACKEND_SETTINGS.EMAIL_NOTIFICATION_SETTINGS: settingUpdate['notify_on_review_status_change'] = value as boolean; break
        case TEAM_BACKEND_SETTINGS.DISABLE_AUTODIALER: settingUpdate['autodialer_disabled'] = value as boolean; break
      }
    }

    getServicesManager().postSettings(settingUpdate).then((success) => {
      if (success && team) {
        let updatedTeam = { ...team }
        for (const [key, value] of updates) {
          switch (key) {
            case TEAM_BACKEND_SETTINGS.DEFAULT_AUDIO_SETTINGS:
              updatedTeam.default_prospect_recording = value as PROSPECT_RECORDING_SETTING;
              break
            case TEAM_BACKEND_SETTINGS.REALTIME_TRANSCRIPTION:
              updatedTeam.default_prospect_streaming_transcription = value as PROSPECT_RECORDING_SETTING;
              break
            case TEAM_BACKEND_SETTINGS.TRANSCRIPTION_RECORDING:
              updatedTeam.default_prospect_transcript_persistence = value as PROSPECT_RECORDING_SETTING;
              break
            case TEAM_BACKEND_SETTINGS.LANGUAGE_SETTING:
              updatedTeam.default_language = value as DEFAULT_LANGUAGE_SETTING;
              break
            case TEAM_BACKEND_SETTINGS.DATA_SEARCH_ENABLED:
              updatedTeam.linkedin_finder_disabled = value as boolean;
              break
            case TEAM_BACKEND_SETTINGS.EMAIL_NOTIFICATION_SETTINGS:
              updatedTeam.notify_on_review_status_change = value as boolean;
              break
            case TEAM_BACKEND_SETTINGS.DISABLE_AUTODIALER:
              updatedTeam.autodialer_disabled = value as boolean;
              break
          }
        }
        dispatch(updateTeam(updatedTeam))
      }
    }).catch((error) => {
      console.error('Failed to update settings:', error)
    }).finally(() => {
      setUpdatingSettings(prev => {
        const newSet = new Set(prev)
        updatingKeys.forEach(key => newSet.delete(key))
        return newSet
      })
    })

    clearCache(SERVICE_CALLS.USER_SETTING)
  }

  const getSettingValue = (setting: TEAM_BACKEND_SETTINGS): any => {
    switch (setting) {
      case TEAM_BACKEND_SETTINGS.DEFAULT_AUDIO_SETTINGS:
        return team?.default_prospect_recording ?? PROSPECT_RECORDING_SETTING.ONE_PARTY_ONLY
      case TEAM_BACKEND_SETTINGS.REALTIME_TRANSCRIPTION:
        return team?.default_prospect_streaming_transcription ?? PROSPECT_RECORDING_SETTING.ALL
      case TEAM_BACKEND_SETTINGS.TRANSCRIPTION_RECORDING:
        return team?.default_prospect_transcript_persistence ?? PROSPECT_RECORDING_SETTING.ALL
      case TEAM_BACKEND_SETTINGS.LANGUAGE_SETTING:
        return team?.default_language ?? DEFAULT_LANGUAGE_SETTING.ENGLISH
      case TEAM_BACKEND_SETTINGS.DATA_SEARCH_ENABLED:
        return team?.linkedin_finder_disabled ?? false
      case TEAM_BACKEND_SETTINGS.EMAIL_NOTIFICATION_SETTINGS:
        return team?.notify_on_review_status_change ?? false
      case TEAM_BACKEND_SETTINGS.DISABLE_AUTODIALER:
        return team?.autodialer_disabled ?? false
      default:
        return null
    }
  }

  const renderBackendSetting = (setting: TEAM_BACKEND_SETTINGS) => {
    const isLoading = updatingSettings.has(setting)

    switch (setting) {
      case TEAM_BACKEND_SETTINGS.DEFAULT_AUDIO_SETTINGS:
      case TEAM_BACKEND_SETTINGS.REALTIME_TRANSCRIPTION:
      case TEAM_BACKEND_SETTINGS.TRANSCRIPTION_RECORDING:
        const value = getSettingValue(setting)
        return <SettingRow label={setting}
          type="select"
          options={Object.values(PROSPECT_RECORDING_SETTING).map((v) => {return {label: PROSPECT_RECORDING_SETTING_TO_LABEL[v], value: v}})}
          value={value}
          setValue={(value) => updateSettingsResult(new Map([[setting, value]]))}
          tooltip={TEAM_SETTING_TOOLTIPS[setting]}
          disabled={isLoading}
          loading={isLoading}
          />
      case TEAM_BACKEND_SETTINGS.LANGUAGE_SETTING:
        return <SettingRow
        label={setting}
        type="select"
        options={Object.values(DEFAULT_LANGUAGE_SETTING).map((v) => {return {label: DEFAULT_LANGUAGE_SETTING_TO_LABEL[v], value: v}})}
        value={getSettingValue(setting)}
        setValue={(value) => updateSettingsResult(new Map([[setting, value]]))}
        tooltip={TEAM_SETTING_TOOLTIPS[setting]}
        disabled={isLoading}
        loading={isLoading}
        />
      case TEAM_BACKEND_SETTINGS.DATA_SEARCH_ENABLED:
      case TEAM_BACKEND_SETTINGS.EMAIL_NOTIFICATION_SETTINGS:
      case TEAM_BACKEND_SETTINGS.DISABLE_AUTODIALER:
        return <SettingRow label={setting} type="toggle"
        value={getSettingValue(setting)}
        setValue={(value) => updateSettingsResult(new Map([[setting, value]]))}
        tooltip={TEAM_SETTING_TOOLTIPS[setting]}
        disabled={isLoading}
        loading={isLoading}
        />
      default:
        return null
    }
  }

  const renderSetting = (setting: TeamSetting) => {
    return renderBackendSetting(setting.backendSetting)
  }

  const renderSettingSection = (section: TeamSettingSection) => {
    return (
      <SettingsSection title={section.label}>
        {section.settings.map((setting, index) => (
          <div key={index}>
            {renderSetting(setting)}
          </div>
        ))}
      </SettingsSection>
    )
  }

  const getTeamSettingsSections = (): TeamSettingSection[] => {
    const teamSections: TeamSettingSection[] = []

    teamSections.push({
      label: 'Team Audio Settings',
      settings: [
        { backendSetting: TEAM_BACKEND_SETTINGS.DEFAULT_AUDIO_SETTINGS },
        { backendSetting: TEAM_BACKEND_SETTINGS.TRANSCRIPTION_RECORDING },
        { backendSetting: TEAM_BACKEND_SETTINGS.REALTIME_TRANSCRIPTION },
        { backendSetting: TEAM_BACKEND_SETTINGS.LANGUAGE_SETTING },
      ]
    })

    teamSections.push({
      label: 'Team Data & Tools',
      settings: [
        { backendSetting: TEAM_BACKEND_SETTINGS.DATA_SEARCH_ENABLED },
        { backendSetting: TEAM_BACKEND_SETTINGS.DISABLE_AUTODIALER },
      ]
    })

    teamSections.push({
      label: 'Team Notifications',
      settings: [
        { backendSetting: TEAM_BACKEND_SETTINGS.EMAIL_NOTIFICATION_SETTINGS },
      ]
    })

    return teamSections
  }

  return (
    <div className="h-full overflow-auto">
      <div className="p-6">
        <div className="mb-6">
          <Typography variant="smallParagraph" color="#6b7280">
            These settings apply to all members of your team
          </Typography>
        </div>

        <div className="flex flex-col gap-6">
          {getTeamSettingsSections().map((section, index) => (
            <div key={index}>
              {renderSettingSection(section)}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}